import * as React from "react";
import {
  BlockTypes,
  ColorNamesBlockModel,
  ImageDirection,
  ColorNamesColorPresetModel,
  LicenseJsonModel,
  CategoryJsonModel,
  MediaSizeTypes,
  BlockJsonModel,
  ColorNamesTextBlockNum,
  GraphicNamesTextBlockNum,
  GraphicNames12CutTextBlockNum
} from "../../models/BlockTransportSystemJsonModel";
import { TextAlignTypes } from "../../models/TextAlignTypes";
import TagBox from "../TagBox/TagBox"
import BlockKitStore from "../../stores/BlockKitStore";
import { inject, observer } from "mobx-react";
import { convertDatetime, getLocaleString } from "../../utils/DateHelper";
import BlockEditBoxImage from "../BlockEditBoxImage/BlockEditBoxImage";
import { CNSSetLayoutStyles } from "../../models/CNSSetLayoutStyles";
import _ from "lodash";
import GlobalEnvironments from "../../constants/GlobalEnvironments";
import { Button, Col, Container, FloatingLabel, Form, FormGroup, Row, Spinner, Stack } from "react-bootstrap";
import { useEffect, useRef, useState } from "react";
import ModalTextColorPreset from "../ModalTextColorPreset/ModalTextColorPreset";
import { productConfigs } from "../../constants/PolarProductList";
import PermissionModal from "../../PermissionModal/PermissionModal";
import StudioUserStore from "../../stores/StudioUserStore";
import { useTranslation } from "react-i18next";
import i18n from "../../../shared/internationalization/I18nSetting";
import { BlockTypeConfig, getBlockTypeConfig, getBlockTypeConfigs } from "../../constants/BlockTypeConfig";

export interface ImageEditState {
  isChanged: boolean;
  hasChanged: boolean;
  file: File;
  fileUrl: string | ArrayBuffer;
  previewLocalUrl: string;
  width?: number;
  height?: number;
}

export interface IBlockEditBoxProps {
  blockKitStore?: BlockKitStore;
  studioUserStore?: StudioUserStore;
  block: BlockJsonModel;
  onBlockListChanged: () => void;
  onClickPublish: () => Promise<void>;
}

const BlockEditBox: React.FC<IBlockEditBoxProps> = inject("blockKitStore", "studioUserStore")(observer(({
  blockKitStore,
  studioUserStore,
  block,
  onBlockListChanged,
  onClickPublish
}) => {
  const inputTag = useRef<HTMLInputElement>(null);
  const defaultImageState = {
    isChanged: false,
    hasChanged: false,
    file: null as File | null,
    fileUrl: "",
    previewLocalUrl: "",
    width: 0,
    height: 0,
  };

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [originImage, setOriginImage] = useState<ImageEditState>({ ...defaultImageState });
  const [previewImage, setPreviewImage] = useState<ImageEditState>({ ...defaultImageState });
  const [iconImage, setIconImage] = useState<ImageEditState>({ ...defaultImageState });
  const [cutImage, setCutImage] = useState<ImageEditState>({ ...defaultImageState });
  const [silverImage, setSilverImage] = useState<ImageEditState>({ ...defaultImageState });
  const [backSilverImage, setBackSilverImage] = useState<ImageEditState>({ ...defaultImageState });
  const [backOriginImage, setBackOriginImage] = useState<ImageEditState>({ ...defaultImageState });
  const [selectedLicense, setSelectedLicense] = useState<LicenseJsonModel>(null);
  const [selectedCategory, setSelectedCategory] = useState<CategoryJsonModel>(null);
  const [currentBlock, setCurrenctBlock] = useState<BlockJsonModel>(block)
  const [isShowTextColorModal, setIsShowTextColorModal] = useState<boolean>();
  const [textColorPreset, setTextColorPreset] = useState<ColorNamesColorPresetModel>();
  const [textColorPresetIndex, setTextColorPresetIndex] = useState<number>(0);
  const [isCreateTextPreset, setIsCreateTextPreset] = useState<boolean>(false);
  const [isShowPermissionModal, setIsShowPermissionModal] = useState<boolean>(false);
  const { t } = useTranslation();
  const [blockTypeConfig, setBlockTypeConfig] = useState<BlockTypeConfig>(getBlockTypeConfig(block.type));
  const [isWorkingPublish, setIsWorkingPublish] = useState<boolean>(false);
  const [isWorkingEdit, setIsWorkingEdit] = useState<boolean>(false);
  const [imageVersion, setImageVersion] = useState<string>(block.updateTime);

  useEffect(() => {
    const license = blockKitStore?.allLicenses?.find((license => license.id === block.licenseId));
    const category = blockKitStore?.allCategories.find((category) => category.id === block.categoryId);
    setSelectedLicense(license);
    setSelectedCategory(category);
  }, [])

  useEffect(() => {
    setCurrenctBlock(block);
    setImageVersion(block.updateTime);
  }, [block])

  // 이미지는 같은 파일명으로 덮어써지므로, 버전을 붙여 브라우저 캐시를 우회한다.
  const getImageUrl = (url: string) => url ? `${GlobalEnvironments.getBtsWebUrl(url)}?v=${imageVersion}` : undefined;

  const handleFileOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let file = e.target.files[0];
    if (!file) return Promise.resolve(null);

    return new Promise(resolve => {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result;
        if (!base64) {
          resolve(null);
          return;
        }

        const img = new Image();
        img.src = base64.toString();
        await img.onload;
        resolve({
          file: file,
          fileUrl: reader.result,
          width: img.width,
          height: img.height,
          previewLocalUrl: URL.createObjectURL(file),
          isChanged: true,
          hasChanged: true
        });
      };
      reader.readAsDataURL(file);
    })
  };

  const replaceAsync = async () => {
    if (!confirm(t("edit-confirmation-message"))) {
      return false;
    }

    if (!currentBlock.categoryId || currentBlock.categoryId === "none") {
      alert("유효한 카테고리를 설정해주세요");
      return false;
    }

    if (currentBlock.dedicatedProducts?.length === 0) {
      alert("노출 제품을 1개 이상 선택해주세요.");
      return false;
    }

    setIsLoading(true);
    const copiedBlock = {
      ...currentBlock
    }
    if (originImage?.isChanged) {
      copiedBlock.originUrl = await blockKitStore.uploadOriginAsync(copiedBlock, originImage?.file);
    }

    if (silverImage?.isChanged) {
      copiedBlock.silverUrl = await blockKitStore.uploadSilverAsync(copiedBlock, silverImage?.file);
    }

    if (backOriginImage?.isChanged) {
      copiedBlock.backOriginUrl = await blockKitStore.uploadBackOriginAsync(copiedBlock, backOriginImage?.file);
    }

    if (backSilverImage?.isChanged) {
      copiedBlock.backSilverUrl = await blockKitStore.uploadBackSilverAsync(copiedBlock, backSilverImage?.file);
    }

    if (previewImage.isChanged) {
      copiedBlock.previewUrl = await blockKitStore.uploadPreviewAsync(
        copiedBlock,
        previewImage.file,
        previewImage.width,
        previewImage.height);
    }

    if (iconImage.isChanged) {
      copiedBlock.iconUrl = await blockKitStore.uploadIconAsync(
        copiedBlock,
        iconImage.file,
        iconImage.width,
        iconImage.height);
    }

    if (cutImage.isChanged) {
      copiedBlock.cutUrl = await blockKitStore.uploadCutAsync(copiedBlock, cutImage.file);
    }

    const response = await blockKitStore?.studioController.postBlockStagingAsync(copiedBlock);
    setIsLoading(false);
    if (!response?.isSuccess) {
      alert(`${t("edit-failed")} (${response?.message})`);
      return false;
    }

    setImageVersion(Date.now().toString());
    setCurrenctBlock(copiedBlock);
    setOriginImage({ ...originImage, isChanged: false });
    setPreviewImage({ ...previewImage, isChanged: false });
    setIconImage({ ...iconImage, isChanged: false });
    setCutImage({ ...cutImage, isChanged: false });
    setSilverImage({ ...silverImage, isChanged: false });
    setBackOriginImage({ ...backOriginImage, isChanged: false });
    setBackSilverImage({ ...backSilverImage, isChanged: false });
    return true;
  }

  const deleteAsync = async () => {
    if (!confirm(t("delete-connfirmation-message"))) return;

    const response = await blockKitStore?.studioController.deleteBlockStagingAsync(block);
    if (response.isSuccess) {
      setCurrenctBlock({
        ...currentBlock,
        deleted: true
      });
    }

    alert(response.message);
  }

  const saveTag = (pressedKey: string, tagWord: string) => {
    if (pressedKey === "Enter" && tagWord.length !== 0) {
      inputTag.current.value = "";
      const tags = [...currentBlock.tags];
      if (tags.some(tag => tag === tagWord)) {
        return;
      }

      tags.push(tagWord);
      setCurrenctBlock({
        ...currentBlock,
        tags: tags
      });
    }
  }

  const deleteColorNamesPreset = (index: number) => {
    let tempPresets = [...(currentBlock.switchableBlockModel as ColorNamesBlockModel).presetModels];
    tempPresets.splice(index, 1);
    setCurrenctBlock({
      ...currentBlock,
      switchableBlockModel: { ...currentBlock.switchableBlockModel, presetModels: tempPresets },
    });
  }

  const editColorNamesPreset = (presetModel: ColorNamesColorPresetModel, presetIndex: number) => {
    let tempPresets = [...(currentBlock.switchableBlockModel as ColorNamesBlockModel).presetModels];
    tempPresets[presetIndex] = presetModel;
    setCurrenctBlock({
      ...currentBlock,
      switchableBlockModel: { ...currentBlock.switchableBlockModel, presetModels: tempPresets },
    });
  }

  const pushTextColorPreset = (model: ColorNamesColorPresetModel) => {
    const switchableBlockModel = currentBlock.switchableBlockModel ?? {
      colorNamesThemeLayout: CNSSetLayoutStyles.NONE,
      presetModels: [],
    }

    let tempPresets = [...((switchableBlockModel as ColorNamesBlockModel)?.presetModels ?? [])];
    tempPresets.push(model);
    setCurrenctBlock({
      ...currentBlock,
      switchableBlockModel: { ...switchableBlockModel, presetModels: tempPresets },
    });
  }

  const textAlignTypes = Object.keys(TextAlignTypes).map((val, idx) => {
    return (
      <option value={val} key={idx}>
        {val}
      </option>
    );
  });

  const getPresetLength = (blockType: BlockTypes) => {
    switch (blockType) {
      case BlockTypes.colorNames: return ColorNamesTextBlockNum;
      case BlockTypes.graphicNames: return GraphicNamesTextBlockNum;
      case BlockTypes.graphicNames12cut: return GraphicNames12CutTextBlockNum;
      default: return 0;
    }
  }

  return (
    <Container fluid className="border rounded p-2">
      {isShowTextColorModal && (
        <ModalTextColorPreset
          onClickCancel={() => {
            setIsShowTextColorModal(false);
          }}
          onClickCreate={(model) => {
            if (isCreateTextPreset) {
              pushTextColorPreset(model);
            } else {
              editColorNamesPreset(model, textColorPresetIndex);
            }
            setIsShowTextColorModal(false);
          }}
          presetEditModel={textColorPreset}
          blockType={currentBlock.type as BlockTypes}
          presetLength={getPresetLength(currentBlock.type as BlockTypes)}
          isShow={isShowTextColorModal}
        />
      )}
      <PermissionModal
        entityType="blocks"
        entity={currentBlock}
        isShow={isShowPermissionModal}
        onClose={() => setIsShowPermissionModal(false)}
      />
      <Stack direction="horizontal" gap={3} className="mb-2">
        <h4>{currentBlock.category} / {currentBlock.name}</h4>
        <Button
          disabled={isWorkingPublish || isWorkingEdit}
          variant="primary"
          className="ms-auto"
          onClick={async () => {
            setIsWorkingPublish(true);
            try {
              await onClickPublish();

            } finally {
              setIsWorkingPublish(false);
            }
          }}>
          {isWorkingPublish &&
            <Spinner
              className="me-1"
              animation="border"
              size="sm"
              role="status"
            />}
          {t("button-publish")}
        </Button>
        <Button
          variant="primary"
          disabled={
            (!originImage.isChanged &&
              !previewImage.isChanged &&
              !iconImage.isChanged &&
              !cutImage.isChanged &&
              !silverImage.isChanged &&
              !backOriginImage.isChanged &&
              !backSilverImage.isChanged &&
              _.isEqual(block, currentBlock) || isWorkingPublish || isWorkingEdit)
          }
          onClick={async (e) => {
            setIsWorkingEdit(true);
            try {
              const permissions = await blockKitStore?.studioController.getEntityPermissionsAsync("blocks", block.id);
              if (!studioUserStore?.hasWritePermission(permissions ?? [])) {
                alert(t("permission-not-allowed-message"));
                return;
              }

              const isSaved = await replaceAsync();
              if (!isSaved) {
                return;
              }

              onBlockListChanged();
              URL.revokeObjectURL(originImage.previewLocalUrl);
              URL.revokeObjectURL(previewImage.previewLocalUrl);
              URL.revokeObjectURL(iconImage.previewLocalUrl);
              URL.revokeObjectURL(cutImage.previewLocalUrl);
              URL.revokeObjectURL(silverImage?.previewLocalUrl);
              URL.revokeObjectURL(backOriginImage?.previewLocalUrl);
              URL.revokeObjectURL(backSilverImage?.previewLocalUrl);
            } catch (error: unknown) {
              setIsLoading(false);
              alert(`${t("edit-failed")} (${blockKitStore?.studioController.getErrorMessage(error).message})`);
            } finally {
              setIsWorkingEdit(false);
            }
          }}
        >
          {isWorkingEdit &&
            <Spinner
              className="me-1"
              animation="border"
              size="sm"
              role="status"
            />}
          {t("button-edit")}
        </Button>
        <Button
          variant="danger"
          onClick={() => {
            deleteAsync();
            onBlockListChanged();
          }}
        >
          {t("button-delete")}
        </Button>
        <Button
          hidden={!studioUserStore?.hasPermission("block:permission")}
          disabled={!currentBlock}
          variant="warning"
          onClick={() => setIsShowPermissionModal(true)}>{t("button-permission")}</Button>
      </Stack>
      <Row>
        <Col>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)"
          }}>
            <BlockEditBoxImage
              id={block.id}
              isLoading={isLoading}
              title={"origin"}
              imgSrc={originImage.isChanged
                ? originImage.previewLocalUrl
                : getImageUrl(currentBlock.originUrl)
              }
              onChange={async (e) => {
                e.preventDefault();
                const result = await handleFileOnChange(e);
                console.log(result);
                if (result) {
                  setOriginImage(result);
                }
              }}
            />
            {(blockTypeConfig?.isCutAvaliable === true || currentBlock.cutUrl) &&
              <BlockEditBoxImage
                id={block.id}
                isLoading={isLoading}
                title={"cut"}
                imgSrc={
                  cutImage.isChanged
                    ? (cutImage.previewLocalUrl)
                    : getImageUrl(currentBlock.cutUrl)
                }
                onChange={async (e) => {
                  e.preventDefault();
                  const result = await handleFileOnChange(e);
                  if (result) {
                    setCutImage(result);
                  }
                }}
              />}
            <BlockEditBoxImage
              id={block.id}
              isLoading={isLoading}
              title={"preview"}
              imgSrc={
                previewImage.isChanged
                  ? previewImage.previewLocalUrl
                  : getImageUrl(currentBlock.previewUrl)
              }
              onChange={async (e) => {
                e.preventDefault();
                const result = await handleFileOnChange(e);
                if (result) {
                  setPreviewImage(result);
                }
              }}
            />
            <BlockEditBoxImage
              id={block.id}
              isLoading={isLoading}
              title={"icon"}
              imgSrc={
                iconImage.isChanged
                  ? iconImage.previewLocalUrl
                  : getImageUrl(currentBlock.iconUrl)
              }
              onChange={async (e) => {
                e.preventDefault();
                const result = await handleFileOnChange(e);
                if (result) {
                  setIconImage(result);
                }
              }}
            />
            {(blockTypeConfig?.isPhotoCardSilverAvaliable === true || currentBlock.silverUrl) &&
              <BlockEditBoxImage
                id={block.id}
                isLoading={isLoading}
                title={"silver"}
                imgSrc={
                  silverImage.isChanged
                    ? silverImage.previewLocalUrl
                    : getImageUrl(currentBlock.silverUrl)
                }
                onChange={async (e) => {
                  e.preventDefault();
                  const result = await handleFileOnChange(e);
                  if (result) {
                    setSilverImage(result);
                  }
                }}
              />}
            {(blockTypeConfig?.isPhotoCardOriginAvaliable === true || currentBlock.backOriginUrl) &&
              <BlockEditBoxImage
                id={block.id}
                isLoading={isLoading}
                title={"backOrigin"}
                imgSrc={
                  backOriginImage.isChanged
                    ? backOriginImage.previewLocalUrl
                    : getImageUrl(currentBlock.backOriginUrl)
                }
                onChange={async (e) => {
                  e.preventDefault();
                  const result = await handleFileOnChange(e);
                  if (result) {
                    setBackOriginImage(result);
                  }
                }}
              />}
            {(blockTypeConfig?.isPhotoCardSilverAvaliable === true || currentBlock.backSilverUrl) &&
              <BlockEditBoxImage
                id={block.id}
                isLoading={isLoading}
                title={"BackSilver"}
                imgSrc={
                  backSilverImage.isChanged
                    ? backSilverImage.previewLocalUrl
                    : getImageUrl(currentBlock.backSilverUrl)
                }
                onChange={async (e) => {
                  e.preventDefault();
                  const result = await handleFileOnChange(e);
                  if (result) {
                    setBackSilverImage(result);
                  }
                }}
              />}
          </div>
        </Col>
        <Col>
          <Form>
            <FormGroup as={Row} className="mb-1">
              <Form.Label column sm="2">ID</Form.Label>
              <Col>
                <Form.Control
                  type="text" plaintext readOnly
                  value={currentBlock.id} />
              </Col>
            </FormGroup>
            <FormGroup as={Row} className="mb-1">
              <Form.Label column sm="2">{t("name")}</Form.Label>
              <Col>
                <Form.Control
                  type="text"
                  value={currentBlock.name}
                  onChange={(e) =>
                    setCurrenctBlock({
                      ...currentBlock,
                      name: e.target.value
                    })
                  } />
              </Col>
            </FormGroup>
            <FormGroup as={Row} className="mb-1">
              <Form.Label column sm="2">{t("subject-name")}</Form.Label>
              <Col>
                <Form.Control
                  type="text"
                  value={currentBlock.subjectName ?? ""}
                  onChange={(e) =>
                    setCurrenctBlock({
                      ...currentBlock,
                      subjectName: e.target.value
                    })
                  } />
              </Col>
            </FormGroup>
            <FormGroup as={Row} className="mb-1">
              <Form.Label column sm="2">{t("category")}</Form.Label>
              <Col>
                <FloatingLabel label={currentBlock?.categoryId}>
                  <Form.Select
                    value={currentBlock.categoryId ?? ""}
                    onChange={(e) => {
                      const category = blockKitStore?.allCategories?.find(
                        (category) => category.id === e.target.value
                      );
                      setSelectedCategory(category);
                      setCurrenctBlock({
                        ...currentBlock,
                        categoryId: category?.id ?? null,
                        category: category?.name ?? null
                      });
                    }}
                  >
                    {blockKitStore?.allCategories?.map((category) => {
                      return (
                        <option value={category.id} key={category.id}>
                          {`${category.lang.find(c => c.key === i18n.language)?.name || category?.name} [${category?.dedicatedProducts && (category.dedicatedProducts.length > 10
                            ? [...category.dedicatedProducts.slice(0, 10), "..."].join(", ")
                            : category.dedicatedProducts.join(", "))}]`}
                        </option>
                      );
                    })}

                  </Form.Select>
                </FloatingLabel>
              </Col>
            </FormGroup>
            <FormGroup as={Row} className="mb-1">
              <Form.Label column sm="2">{t("license")}</Form.Label>
              <Col>
                <FloatingLabel label={currentBlock?.licenseId}>
                  <Form.Select
                    value={currentBlock.licenseId ?? "none"}
                    onChange={(e) => {
                      const license = blockKitStore?.allLicenses?.find(
                        (license) => license.id === e.target.value
                      );
                      setSelectedLicense(license);
                      setCurrenctBlock({
                        ...currentBlock,
                        licenseId: license?.id ?? null,
                        license: license?.name ?? null
                      });
                    }}
                  >
                    <option value="none" key={"none"}>{t("license-none")}</option>
                    {blockKitStore?.allLicenses?.map((license) => {
                      return (
                        <option value={license.id} key={license.id}>{license.name}</option>
                      );
                    })}
                  </Form.Select>
                </FloatingLabel>
              </Col>
            </FormGroup>
            <FormGroup as={Row} className="mb-1">
              <Form.Label column sm="2">{t("tag")}</Form.Label>
              <Col>
                <Form.Control
                  placeholder={t("tag-input-message")}
                  type="text"
                  ref={inputTag}
                  onKeyDown={(e) => saveTag(e.key, e.currentTarget.value)}
                />
              </Col>
            </FormGroup>
            {currentBlock.tags?.length > 0 && (
              <Stack direction="horizontal" gap={2}>
                {currentBlock.tags?.map((value, index) => (
                  <TagBox
                    key={index}
                    tag={"#" + value}
                    onClickDelete={() => {
                      setCurrenctBlock({
                        ...currentBlock,
                        tags: currentBlock.tags.filter(tag => tag !== value)
                      });
                    }} />
                ))}
              </Stack>
            )}
            <FormGroup as={Row} className="mb-1">
              <Form.Label column sm="2">{t("priority")}</Form.Label>
              <Col>
                <Form.Control
                  min={1}
                  type="number"
                  value={currentBlock.priority}
                  onChange={(e) => {
                    setCurrenctBlock({
                      ...currentBlock,
                      priority: Number(e.target.value)
                    });
                  }} />
              </Col>
            </FormGroup>
            <FormGroup as={Row} className="mb-1">
              <Form.Label column sm="2">{t("price")}</Form.Label>
              <Col>
                <Form.Control
                  type="number"
                  readOnly
                  value={currentBlock.price} />
              </Col>
            </FormGroup>
            <FormGroup as={Row} className="mb-1">
              <Form.Label column sm="2">{t("price-by-country")}</Form.Label>
              <Col>
                <Form.Control
                  type="text"
                  readOnly
                  value={currentBlock.prices?.map(i => `${i.locale}(${i.price})`).join("\n")} />
              </Col>
            </FormGroup>
            <FormGroup as={Row} className="mb-1">
              <Form.Label column sm="2">{t("media-size")}</Form.Label>
              <Col>
                <Form.Select
                  value={currentBlock.mediaSize}
                  onChange={(e) => {
                    setCurrenctBlock({
                      ...currentBlock,
                      mediaSize: e.target.value
                    });
                  }}
                >
                  <option value={""} key={"none"}>{t("none")}</option>
                  {Object.values(MediaSizeTypes).map((val, idx) => {
                    return (
                      <option value={val} key={idx}>{val}</option>
                    );
                  })}
                </Form.Select>
              </Col>
            </FormGroup>
            <FormGroup as={Row} className="mb-1">
              <Form.Label column sm="2">{t("block-type")}</Form.Label>
              <Col>
                <Form.Select
                  disabled
                  value={currentBlock.type}
                  onChange={(e) => {
                    setCurrenctBlock({
                      ...currentBlock,
                      type: e.target.value as BlockTypes
                    });
                  }}
                >
                  {getBlockTypeConfigs().map((val) => {
                    return (
                      <option value={val.blockType} key={val.blockType}>{val.blockType}</option>
                    );
                  })}
                </Form.Select>
              </Col>
            </FormGroup>
            <FormGroup as={Row} className="mb-1" hidden={blockTypeConfig?.isTextColorPresetAvaliable !== true}>
              <Form.Label column sm="2">{t("color-preset")}</Form.Label>
              <Col>
                <Stack direction="horizontal" gap={2}>
                  {(currentBlock.switchableBlockModel as ColorNamesBlockModel)
                    ?.presetModels?.map((value, presetIndex) => (
                      <TagBox
                        key={presetIndex}
                        tag={"#" + value.presetName}
                        onClickDelete={() =>
                          deleteColorNamesPreset(presetIndex)
                        }
                        onClickTag={() => {
                          setTextColorPreset(value);
                          setTextColorPresetIndex(presetIndex);
                          setIsCreateTextPreset(false);
                          setIsShowTextColorModal(true);
                        }}
                      />
                    ))}
                  <Button
                    variant="outline-secondary"
                    onClick={() => {
                      setTextColorPreset(undefined);
                      setIsCreateTextPreset(true);
                      setIsShowTextColorModal(true);
                    }}
                  >
                    {t("add")}
                  </Button>
                </Stack>
              </Col>
            </FormGroup>
            <FormGroup as={Row} className="mb-1" hidden={blockTypeConfig?.isColorNamesLayoutAvaliable !== true}>
              <Form.Label column sm="2">{t("colornames-layout")}</Form.Label>
              <Col>
                <Form.Select
                  value={(currentBlock?.switchableBlockModel as ColorNamesBlockModel)?.colorNamesThemeLayout}
                  onChange={(e) => {
                    const themeLayout = {
                      ...(currentBlock.switchableBlockModel as ColorNamesBlockModel),
                      colorNamesThemeLayout: e.target.value as CNSSetLayoutStyles
                    };
                    setCurrenctBlock({
                      ...currentBlock,
                      switchableBlockModel: themeLayout
                    });
                  }}
                >
                  {/** 마이그레이션 전까지 기본이 undefined인 블록들에 대해 표현해주기 위한 disabled option*/}
                  <option disabled label="-- select Oprion --" />
                  {Object.keys(CNSSetLayoutStyles).map((val, idx) => {
                    return (
                      <option value={val} key={idx}>{val}</option>
                    );
                  })}
                </Form.Select>
              </Col>
            </FormGroup>
            <FormGroup as={Row} className="mb-1" hidden={blockTypeConfig?.isTextAlignAvaliable !== true}>
              <Form.Label column sm="2">{t("text-align")}</Form.Label>
              <Col>
                <Form.Select
                  value={currentBlock.textAlignType}
                  onChange={(e) => {
                    setCurrenctBlock({
                      ...currentBlock,
                      textAlignType: e.target.value as TextAlignTypes
                    });
                  }}
                >
                  {textAlignTypes}
                </Form.Select>
              </Col>
            </FormGroup>
            <FormGroup as={Row} className="mb-1">
              <Form.Label column sm="2">{t("layout")}</Form.Label>
              <Col>
                <Form.Select
                  value={currentBlock?.layoutId}
                  onChange={(e) => {
                    const layout = blockKitStore?.layouts.find(i => i.layoutId === e.target.value);
                    setCurrenctBlock({
                      ...currentBlock,
                      layoutId: layout?.layoutId,
                      layoutType: layout?.layoutType
                    });
                  }}
                >
                  <option value={"none"} key={"none"}>{"none"}</option>
                  {blockKitStore?.layouts
                    ?.filter(i => i.blockType === currentBlock.type)
                    ?.map(i => {
                      return <option key={i.layoutId} value={i.layoutId}>{`${i.layoutType}`}</option>
                    })
                  }
                </Form.Select>
              </Col>
            </FormGroup>
            <FormGroup as={Row} className="mb-1">
              <Form.Label column sm="2">{t("image-direction")}</Form.Label>
              <Col>
                <Form.Select
                  value={currentBlock?.imageDirection}
                  onChange={(e) => {
                    setCurrenctBlock({
                      ...currentBlock,
                      imageDirection: e.target.value as ImageDirection
                    });
                  }}
                >
                  <option disabled label="-- select Oprion --" />
                  {Object.keys(ImageDirection).map((val, idx) => {
                    return (
                      <option value={val} key={idx}>{val}</option>
                    );
                  })}
                </Form.Select>
              </Col>
            </FormGroup>
            <FormGroup as={Row} className="mb-1">
              <Form.Label column sm="2">{t("description")}</Form.Label>
              <Col>
                <Form.Control
                  as={"textarea"}
                  rows={2}
                  value={currentBlock.description}
                  onChange={(e) => {
                    setCurrenctBlock({
                      ...currentBlock,
                      description: e.target.value
                    });
                  }}
                />
              </Col>
            </FormGroup>
            <FormGroup as={Row} className="mb-1">
              <Form.Label column sm="2">{t("release-date")}</Form.Label>
              <Col>
                <Form.Control
                  type="datetime-local"
                  onChange={(e) => {
                    setCurrenctBlock({
                      ...currentBlock,
                      releaseDate: new Date(e.target.value),
                    });
                  }}
                  value={
                    currentBlock.releaseDate
                      ? convertDatetime(new Date(currentBlock.releaseDate), blockKitStore?.timeZone)
                      : ""
                  }
                />
              </Col>
            </FormGroup>
            <FormGroup as={Row} className="mb-1">
              <Form.Label column sm="2">{t("expire-date")}</Form.Label>
              <Col>
                <Form.Control
                  type="datetime-local"
                  onChange={(e) => {
                    setCurrenctBlock({
                      ...currentBlock,
                      expireDate: new Date(e.target.value),
                    });
                  }}
                  value={
                    currentBlock.expireDate
                      ? convertDatetime(new Date(currentBlock.expireDate), blockKitStore?.timeZone)
                      : ""
                  }
                />
              </Col>
            </FormGroup>
            <FormGroup as={Row} className="mb-1" hidden={(selectedCategory?.probabilityTables?.length ?? 0) === 0}>
              <Form.Label column sm="2">{t("probability-grade")}</Form.Label>
              <Col>
                <Form.Select
                  value={currentBlock.grade}
                  onChange={(e) => {
                    setCurrenctBlock({
                      ...currentBlock,
                      grade: parseInt(e.target.value),
                    });
                  }}
                >
                  <option value={1} key={1}>1</option>
                  <option value={2} key={2}>2</option>
                  <option value={3} key={3}>3</option>
                  <option value={4} key={4}>4</option>
                  <option value={5} key={5}>5</option>
                </Form.Select>
              </Col>
            </FormGroup>
            <Form.Group>
              <Form.Label>{t("dedicated-product")}</Form.Label>
              <Stack direction="horizontal" gap={1} className="flex-wrap">
                {productConfigs
                  .filter(i => blockTypeConfig?.productCategories?.includes(i.productCategory) || currentBlock.dedicatedProducts?.includes(i.productType))
                  .map((value, index) => {
                    return (
                      <Button
                        variant={currentBlock.dedicatedProducts?.includes(value.productType) ? "outline-primary" : "outline-secondary"}
                        size="sm"
                        key={`${block.id}:${value.productType}`}
                        id={`${block.id}:${value.productType}`}
                        value={value.productType}
                        active={currentBlock.dedicatedProducts?.includes(value.productType)}
                        onClick={() => {
                          const copiedProduct = [...(currentBlock.dedicatedProducts ?? [])];
                          const index = copiedProduct.indexOf(value.productType);
                          if (index < 0) {
                            copiedProduct.push(value.productType);
                          } else {
                            copiedProduct.splice(index, 1);
                          }
                          setCurrenctBlock({
                            ...currentBlock,
                            dedicatedProducts: copiedProduct,
                          });
                        }}
                      >
                        {value.productType}
                      </Button>
                    );
                  })}
              </Stack>
            </Form.Group>
            <Stack direction="horizontal">
              <Form.Label column sm="2">{t("disable")}</Form.Label>
              <Form.Check
                type="switch"
                checked={currentBlock.disabled}
                onChange={(e) => {
                  setCurrenctBlock({
                    ...currentBlock,
                    disabled: e.target.checked
                  });
                }} />
            </Stack>
            <FormGroup as={Row}>
              <Form.Label column sm="2">{t("created-date")}</Form.Label>
              <Col>
                <Form.Control
                  type="text" plaintext readOnly
                  value={getLocaleString(new Date(currentBlock.createTime), blockKitStore?.timeZone)} />
              </Col>
              <Form.Label column sm="2">{t("modified-date")}</Form.Label>
              <Col>
                <Form.Control
                  type="text" plaintext readOnly
                  value={getLocaleString(new Date(currentBlock.updateTime), blockKitStore?.timeZone)} />
              </Col>
            </FormGroup>
            <FormGroup as={Row}>
            </FormGroup>
          </Form>
        </Col>
      </Row>
    </Container>
  );
}));

export default BlockEditBox;
